import { computed, effect, Injector, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { SchemaTargetDto } from 'src/app/services/api.services';
import { filterToDistinct, group, isNullOrUndefined } from 'src/app/shared/utils';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { distinctValues } from '../../shared/utils/distinctValues';
import { DataSchema } from '../dataModel';
import { deepEquals } from '../utils/compare';
import { logger } from '../utils/logger';
import { VisualComponent } from './base/Visual.component';
import { VisualField } from './VisualField';
import { VisualFieldTarget } from './VisualFieldTarget';
import { VisualFilter } from './VisualFilter';
import { VisualHost } from './VisualHost';

const LOGGER = logger('VisualInteractions');

export class VisualInteractions {
  constructor(
    readonly injector: Injector,
    readonly visualHosts?: Signal<ReadonlyArray<VisualHost>>) { }
  
  /**
    * Additional filters applied to all visuals.
    * These applies before request is made.
    **/
  readonly requestFilters = new class {
    constructor(readonly owner: VisualInteractions) { }

    readonly all = signal<ReadonlyArray<VisualFilter>>([]);
    readonly bySchema = new SchemaMap(this.owner.injector, this.all, x => x.target().schema.getSchemaTargetDto());

    set(value: ReadonlyArray<VisualFilter>) {
      untracked(() => {
        this.all.set(value);
      });
    }
  }(this);

  /**
   * Filtering per data point.
   * This usually affects cross-filtering/highlight.
   **/
  readonly crossFilter = new class {
    constructor(readonly owner: VisualInteractions) { }

    readonly source = signal<IVisualInteractionSource>(null);

    readonly groups = computed(() => this.source()?.crossFilterGroups() ?? []);
  }(this);
}


export class CrossFilterGroup {
  constructor(
    readonly schema: DataSchema,
    readonly filters: ReadonlyArray<DataPoint>) { }
}

export namespace CrossFilterGroup {
  export const same = (a: CrossFilterGroup, b: CrossFilterGroup) => {
    if (a === b)
      return true;

    if (!a || !b)
      return (!a) === (!b);

    if (a.schema !== b.schema)
      return false;

    return DataPoint.arrayEqual(a.filters, b.filters);
  }

  export const arrayEqual = (a: ReadonlyArray<CrossFilterGroup>, b: ReadonlyArray<CrossFilterGroup>) => {
    if (a === b)
      return true;

    if (!a || !b)
      return (!a) === (!b);
      
    if (a.length !== b.length)
      return false;

    let aSet = a.filter(x => b.some(y => CrossFilterGroup.same(x, y)));
    if (aSet.length != a.length)
      return false;

    let bSet = b.filter(x => a.some(y => CrossFilterGroup.same(x, y)));
    if (bSet.length != b.length)
      return false;

    return true;
  };
}


export interface IVisualInteractionSource {
  get dataPointSelection(): DataPointSet;
  crossFilterGroups(): ReadonlyArray<CrossFilterGroup>;

  getFormatter(): VisualComponent.Formatter;
}

export class SchemaMap<T> {
  constructor(
    readonly injector: Injector,
    readonly all: Signal<ReadonlyArray<T>>,
    readonly getSchema: (x: T) => SchemaTargetDto,
    readonly equal?: (x: ReadonlyArray<T>, y: ReadonlyArray<T>) => boolean) { }
  
  private readonly _map = new Map<string, WritableSignal<ReadonlyArray<T>>>();
  private readonly _mapUpdate = effect(() => {
    let _ = this.all();
    untracked(() => this.update());
  }, { injector: this.injector });

  private update() {
    let bySchema = group(
      this.all(),
      x => SchemaUtils.getTargetString(this.getSchema(x)).toLowerCase());

    for (let signal of this._map.values())
      signal.set([]);

    for (let { key, items } of bySchema) {
      let s = this._map.get(key);
      if (!s) {
        s = signal(items);
        this._map.set(key, s);
      }
      else {
        s.set(items);
      }
    }
  }

  get(schema: DataSchema) {
    let key = SchemaUtils.getTargetString(schema.getSchemaTargetDto()).toLowerCase();
    let s = this._map.get(key);

    if (!s) {
      s = signal([], { equal: this.equal });
      this._map.set(key, s);
    }

    return s;
  }
}

export interface DataPointField {
  readonly field: VisualField;
  readonly value: any;
}

const NULL_KEY = {};
const ROOT_KEY = {};

let nextDataPointId = 0;

export class DataPoint {
  readonly id = nextDataPointId++;

  constructor(
    readonly collection: DataPointCollection,
    readonly field:      VisualField,
    readonly key:        any,
    readonly value:      any,
    readonly parent?:    DataPoint) { }

  private readonly _children = new Map<VisualField, DataPoint[]>();

  get children() { return this._children.values(); }

  getChildrenByField(field: VisualField) {
    return this._children.get(field);
  }

  getChild({ field, value }: DataPointField) {
    let key = isNullOrUndefined(value) ? NULL_KEY : value;

    let targetValues = this._children.get(field);
    if (!targetValues) {
      targetValues = [];
      this._children.set(field, targetValues);
    }

    let index = targetValues.findIndex(x => x.key === key);
    if (index === -1) {
      index = targetValues.length;
      targetValues.push(new DataPoint(this.collection, field, key, value, this));
    }

    return targetValues[index];
  }

  getDescendant(fields: ReadonlyArray<DataPointField>) {
    let n: DataPoint = this;

    for (let i = 0; i < fields.length; ++i)
      n = n.getChild(fields[i]);

    return n;
  }

  findParent(callback: (dp: DataPoint) => boolean) {
    for (let dp: DataPoint = this; dp.parent; dp = dp.parent) {
      if (callback(dp))
        return dp;
    }

    return null;
  }

  visitSelfAndParents(filter: (dp: DataPoint) => boolean): DataPoint[] {
    let list: DataPoint[] = [];
    let n: DataPoint = this;

    while (n) {
      if (filter(n))
        list.push(n);

      n = n.parent;
    }
    
    return list;
  }

  clear() {
    this._children.clear();
  }

  private _valueDisplay: string;

  get valueDisplay(): string {
    return this._valueDisplay ??= this.collection.source.getFormatter().getValueDisplay(this.field, this.value);
  }
}

export class DataPointCollection {
  constructor(readonly source: IVisualInteractionSource) { }

  private readonly _root = new DataPoint(this, null, ROOT_KEY, null, null);

  get(fields: ReadonlyArray<DataPointField>): DataPoint {
    return untracked(() => {
      if (!fields?.length)
        throw new Error(`Invalid field list`);

      let sortedFields = [...fields].sort((a, b) => {
        let d = a.field.role().kind - b.field.role().kind;
        if (d === 0) {
          d = b.field.role().id - b.field.role().id;

          if (d === 0) {
            d = a.field.roleIndex() - b.field.roleIndex();
          }
        }

        return d;
      });

      return this._root.getDescendant(sortedFields);
    });
  }

  getValue(parent: DataPoint, field: DataPointField): DataPoint {
    return untracked(() => parent.getChild(field));
  }

  clear() {
    this._root.clear();
  }
}

export class DataPointSet {
  constructor() { }

  private readonly _version = signal(0);
  private readonly _set     = new Set<DataPoint>();

  readonly size = computed(() => {
    let _ = this._version();
    return this._set.size;
  });

  private readonly _values = computed(() => {
    return { v: this._version(), s: this._set };
  }, { equal: (a, b) => a.v === b.v });

  values() {
    return this._values().s;
  }

  has(p: DataPoint): boolean {
    let _ = this._version();
    return this._set.has(p);
  }

  toggle(p: DataPoint, set: boolean) {
    if (set) {
      let oldSize = this._set.size;
      this._set.add(p);
      
      if (oldSize < this._set.size) {
        this.bumpVersion();
        return true;
      }
    }
    else {
      if (this._set.delete(p)) {
        this.bumpVersion();
        return true;
      }
    }

    return false;
  }

  add(p: DataPoint): boolean {
    return this.toggle(p, true);
  }

  delete(p: DataPoint): boolean {
    return this.toggle(p, false);
  }

  clear(): boolean {
    if (this._set.size === 0)
      return false;

    this._set.clear();
    this.bumpVersion();
    return true;
  }

  set(points: ReadonlyArray<DataPoint>): boolean {
    let newValues = new Set(points);
    let originalSize = this._set.size;

    if (originalSize === newValues.size) {
      if (originalSize === 0)
        return false;

      let allFound = true;

      for (let p of newValues) {
        if (!this._set.has(p)) {
          allFound = false;
          break;
        }
      }

      if (allFound)
        return false;
    }

    this._set.clear();

    for (let p of newValues)
      this._set.add(p);

    this.bumpVersion();
    return true;
  }

  private bumpVersion() {
    this._version.update(x => x + 1);
  }
}


export namespace DataPoint {
  export function getValues(dataPoints: ReadonlyArray<DataPoint>): DataPointValues[] {
    let values: DataPointValues[] = [];

    for (let g of group(dataPoints, x => x.field)) {
      values.push({
        target: g.key.target,
        values: filterToDistinct(g.items.map(x => x.value))
      });
    }

    return values;
  }

  export const arrayEqual = (a: ReadonlyArray<DataPoint>, b: ReadonlyArray<DataPoint>) => {
    if (a === b)
      return true;

    if (isNullOrUndefined(a) || a.length === 0)
      return isNullOrUndefined(b) || b.length === 0;

    let setA = new Set(a);
    let setB = new Set(b);

    if (setA.size != setB.size)
      return false;

    for (let i of setA) {
      if (!setB.has(i))
        return false;
    }

    return true;
  };
}

export interface DataPointValues {
  readonly target: VisualFieldTarget;
  readonly values: ReadonlyArray<any>;
}

export namespace DataPointValues {
  export const arrayEqual = (a: ReadonlyArray<DataPointValues>, b: ReadonlyArray<DataPointValues>) => {
    if (a === b)
      return true;

    if (isNullOrUndefined(a) || a.length === 0)
      return isNullOrUndefined(b) || b.length === 0;

    if (a.length !== b.length)
      return false;

    return untracked(() => {
      let sortedA = [...a].sort((a, b) => a.target.referenceString().localeCompare(b.target.referenceString()));
      let sortedB = [...b].sort((a, b) => a.target.referenceString().localeCompare(b.target.referenceString()));

      for (let i = 0; i < sortedA.length; ++i) {
        let itemA = sortedA[i];
        let itemB = sortedB[i];
        if (!itemA.target.equals(itemB.target))
          return false;

        let valuesA = [...itemA.values].sort();
        let valuesB = [...itemB.values].sort();

        if (!deepEquals(valuesA, valuesB))
          return false;
      }

      return true;
    });
  };
}
